import { uid, extend, isDefined } from "@dhx/ts-common/core";
import { el, inject } from "@dhx/ts-common/dom";
import { IViewLike, View } from "@dhx/ts-common/view";

import { ICell, ICellConfig, ILayout, resizeMode, LayoutEvents, ILayoutEventHandlersMap } from "./types";
import { getBlockRange, getResizeMode } from "./helpers";
import { IEventSystem, EventSystem } from "@dhx/ts-common/events";


export class Cell extends View implements ICell {
	public id: string;
	public config: ICellConfig;
	public events: IEventSystem<LayoutEvents, ILayoutEventHandlersMap>;
	protected _handlers: { [key: string]: (...args: any) => any };

	// FIXME
	// actually, for Layout parent can be ICell as well
	private _parent: ILayout;
	private _ui: IViewLike;

	private _resizerHandlers: any;

	constructor(parent: string | HTMLElement | ILayout, config: ICellConfig) { // asChild for detect parent object
		super(parent, extend({ gravity: true }, config));

		const p = parent as ILayout;
		if (p && p.isVisible) {
			this._parent = p;
		}

		if (this._parent && this._parent.events) {
			this.events = this._parent.events;
		} else {
			this.events = new EventSystem(this);
		}

		this.config.full = this.config.full === undefined ? Boolean(this.config.header || this.config.collapsable) : this.config.full;

		this._initHandlers();

		this.id = this.config.id || uid();
	}
	paint() {
		if (this.isVisible()) {
			const view = this.getRootView();
			if (view) {
				view.redraw();
			} else {
				this._parent.paint();
			}
		}
	}
	isVisible() {
		// top level node
		if (!this._parent) {
			if (this._container && this._container.tagName) {
				return true;
			}
			return Boolean(this.getRootNode());
		}

		// check active view in case of multiview
		const active = this._parent.config.activeView;
		if (active && active !== this.id) {
			return false;
		}

		// check that all parents of the cell are visible as well
		return !this.config.hidden && (!this._parent || this._parent.isVisible());
	}
	hide() {
		if (!this.events.fire(LayoutEvents.beforeHide, [this.id])) {
			return;
		}

		this.config.hidden = true;
		if (this._parent && this._parent.paint) {
			this._parent.paint();
		}

		this.events.fire(LayoutEvents.afterHide, [this.id]);
	}
	show() {
		if (!this.events.fire(LayoutEvents.beforeShow, [this.id])) {
			return;
		}

		if (this._parent && this._parent.config.activeView) {
			this._parent.config.activeView = this.id;
		} else {
			this.config.hidden = false;
		}

		if (this._parent && !this._parent.isVisible()) {
			this._parent.show();
		}

		this.paint();

		this.events.fire(LayoutEvents.afterHide, [this.id]);
	}
	getParent() {
		return this._parent;
	}
	destructor() {
		this.config = null;
		this.unmount();
	}
	getWidget() {
		return this._ui;
	}
	getCellView() {
		return this._parent && this._parent.getRefs(this._uid);
	}
	attach(name: any, config?: any): IViewLike {
		this.config.html = null;
		if (typeof name === "object") {
			this._ui = name;
		} else if (typeof name === "string") {
			this._ui = new (window as any).dhx[name](null, config);
		} else if (typeof name === "function") {
			if (name.prototype instanceof View) {
				this._ui = new name(null, config);
			} else {
				this._ui = {
					getRootView() {
						return name(config);
					}
				};
			}
		}
		this.paint();
		return this._ui;
	}
	attachHTML(html: string): void {
		this.config.html = html;
		this.paint();
	}
	toVDOM(nodes?: any[]) {
		if (this.config === null) {
			this.config = {};
		}
		if (this.config.hidden) {
			return;
		}
		const style = this._calculateStyle();
		const stylePadding = isDefined(this.config.padding) ? { padding: this.config.padding } : {};
		let kids;
		if (!this.config.html) {
			if (this._ui) {
				let view = this._ui.getRootView();
				if (view.render) {
					view = inject(view);
				}
				kids = [view];
			} else {
				kids = nodes || null;
			}
		}
		const resizer = this.config.resizable && !this._isLastCell() && !this.config.collapsed ?
			el(".dhx_layout-resizer." + (this._isXDirection() ? "dhx_layout-resizer--x" : "dhx_layout-resizer--y"), {
				...this._resizerHandlers,
				_ref: "resizer_" + this._uid
			}, [el("span.dhx_layout-resizer__icon", {
				class: "dxi " + (this._isXDirection() ? "dxi-dots-vertical" : "dxi-dots-horizontal")
			})]) : null;

		const handlers = {};
		if (this.config.on) {
			for (const key in this.config.on) {
				handlers["on" + key] = this.config.on[key];
			}
		}
		const cell = el(
			"div",
			{
				_key: this._uid,
				style: this.config.full || this.config.html ? style : { ...style, ...stylePadding },
				_ref: this._uid,
				["aria-labelledby"]: this.config.id ? "tab-content-" + this.config.id : null,
				...handlers,
				class: this._getCss(false) +
					(this.config.css ? " " + this.config.css : "") +
					(this.config.collapsed ? " dhx_layout-cell--collapsed" : "") +
					(this.config.resizable ? " dhx_layout-cell--resizeble" : "") +
					// for cells only
					(this.config.gravity ? " dhx_layout-cell--gravity" : ""),
			},
			this.config.full ? [
				el("div", {
					tabindex: this.config.collapsable ? "0" : "-1",
					class: "dhx_layout-cell-header" +
						(this._isXDirection() ? " dhx_layout-cell-header--col" : " dhx_layout-cell-header--row") +
						(this.config.collapsable ? " dhx_layout-cell-header--collapseble" : "") +
						(this.config.collapsed ? " dhx_layout-cell-header--collapsed" : "") +
						(((this.getParent() || {} as any).config || {} as any).isAccordion ? " dhx_layout-cell-header--accordion" : ""),
					onclick: this._handlers.collapse,
					onkeydown: this._handlers.enterCollapse
				}, [
					this.config.headerIcon && el("span.dhx_layout-cell-header__icon" + this.config.headerIcon),
					this.config.headerImage && el(".dhx_layout-cell-header__image-wrapper", [
						el("img", {
							src: this.config.headerImage,
							class: "dhx_layout-cell-header__image",
						})
					]),
					this.config.header && el("h3.dhx_layout-cell-header__title", this.config.header),
					this.config.collapsable && el("div.dhx_layout-cell-header__collapse-icon", {
						class: this._getCollapseIcon()
					}),
				]),
				!this.config.collapsed ? el("div", {
					"style": stylePadding,
					".innerHTML": this.config.html,
					"class": this._getCss(true) + " dhx_layout-cell-content",
				}, kids) : null
			] : (this.config.html ? [
				el(".dhx_layout-cell-content", {
					".innerHTML": this.config.html,
					"style": stylePadding,
				})
			] : kids)
		);

		return resizer ? [
			cell,
			resizer
		] : cell;
	}
	protected _getCss(_content?: boolean) {
		return "dhx_layout-cell";
	}
	protected _initHandlers() {
		this._handlers = {
			enterCollapse: (e: KeyboardEvent) => {
				if (e.keyCode === 13) {
					this._handlers.collapse();
				}
			},
			collapse: () => {
				if (!this.config.collapsable) {
					return;
				}

				if (!this.events.fire(LayoutEvents.beforeCollapse, [this.id])) {
					return;
				}

				this.config.collapsed = !this.config.collapsed;
				this.paint();

				this.events.fire(LayoutEvents.afterCollapse, [this.id]);
			}
		};
		const blockOpts = {
			left: null,
			top: null,
			isActive: false,
			range: null,
			xLayout: null,
			nextCell: null,
			size: null,
			resizerLength: null,
			mode: null,
			percentsum: null
		};
		const mouseUp = () => {
			blockOpts.isActive = false;
			document.body.classList.remove("dhx_no-select--resize");
			document.removeEventListener("mouseup", mouseUp);
			document.removeEventListener("mousemove", mouseMove);

			this.events.fire(LayoutEvents.afterResizeEnd, [this.id]);
		};
		const mouseMove = e => {
			if (!blockOpts.isActive || blockOpts.mode === resizeMode.unknown) {
				return;
			}
			let newValue = blockOpts.xLayout
				? e.x - blockOpts.range.min + window.pageXOffset
				: e.y - blockOpts.range.min + window.pageYOffset;
			const prop = blockOpts.xLayout ? "width" : "height";
			if (newValue < 0) {
				newValue = blockOpts.resizerLength / 2;
			} else if (newValue > blockOpts.size) {
				newValue = blockOpts.size - blockOpts.resizerLength;
			}

			switch (blockOpts.mode) {
				case resizeMode.pixels:
					this.config[prop] = newValue - blockOpts.resizerLength / 2 + "px";
					blockOpts.nextCell.config[prop] = blockOpts.size - newValue - blockOpts.resizerLength / 2 + "px";
					break;
				case resizeMode.mixedpx1:
					this.config[prop] = newValue - blockOpts.resizerLength / 2 + "px";
					break;
				case resizeMode.mixedpx2:
					blockOpts.nextCell.config[prop] = blockOpts.size - newValue - blockOpts.resizerLength / 2 + "px";
					break;
				case resizeMode.percents:
					this.config[prop] = newValue / blockOpts.size * blockOpts.percentsum + "%";
					blockOpts.nextCell.config[prop] = (blockOpts.size - newValue) / blockOpts.size * blockOpts.percentsum + "%";
					break;
				case resizeMode.mixedperc1:
					this.config[prop] = newValue / blockOpts.size * blockOpts.percentsum + "%";
					break;
				case resizeMode.mixedperc2:
					blockOpts.nextCell.config[prop] = (blockOpts.size - newValue) / blockOpts.size * blockOpts.percentsum + "%";
					break;
			}
			this.paint();
			this.events.fire(LayoutEvents.resize, [this.id]);
		};

		this._resizerHandlers = {
			onmousedown: (e) => {
				if (e.which === 3) {
					return;
				}
				if (blockOpts.isActive) {
					mouseUp();
				}

				if (!this.events.fire(LayoutEvents.beforeResizeStart, [this.id])) {
					return;
				}

				document.body.classList.add("dhx_no-select--resize");

				const block = this.getCellView();
				const nextCell = this._getNextCell();
				const nextBlock = nextCell.getCellView();
				const resizerBlock = this._getResizerView();
				const blockOffsets = block.el.getBoundingClientRect();
				const resizerOffsets = resizerBlock.el.getBoundingClientRect();
				const nextBlockOffsets = nextBlock.el.getBoundingClientRect();

				blockOpts.xLayout = this._isXDirection();

				blockOpts.left = blockOffsets.left + window.pageXOffset;
				blockOpts.top = blockOffsets.top + window.pageYOffset;

				blockOpts.range = getBlockRange(blockOffsets, nextBlockOffsets, blockOpts.xLayout);
				blockOpts.size = blockOpts.range.max - blockOpts.range.min;
				blockOpts.isActive = true;
				blockOpts.nextCell = nextCell;
				blockOpts.resizerLength = blockOpts.xLayout ? resizerOffsets.width : resizerOffsets.height;

				blockOpts.mode = getResizeMode(blockOpts.xLayout, this.config, nextCell.config);
				if (blockOpts.mode === resizeMode.percents) {
					const field = blockOpts.xLayout ? "width" : "height";
					blockOpts.percentsum = parseFloat(this.config[field]) + parseFloat(nextCell.config[field]);
				}
				if (blockOpts.mode === resizeMode.mixedperc1) {
					const field = blockOpts.xLayout ? "width" : "height";
					blockOpts.percentsum = 1 / (blockOffsets[field] / (blockOpts.size - blockOpts.resizerLength)) * parseFloat(this.config[field]);
				}
				if (blockOpts.mode === resizeMode.mixedperc2) {
					const field = blockOpts.xLayout ? "width" : "height";
					blockOpts.percentsum = 1 / (nextBlockOffsets[field] / (blockOpts.size - blockOpts.resizerLength)) * parseFloat(nextCell.config[field]);
				}

				document.addEventListener("mouseup", mouseUp);
				document.addEventListener("mousemove", mouseMove);
			},
			ondragstart: e => e.preventDefault()
		};
	}
	private _getCollapseIcon() {
		if (this._isXDirection() && this.config.collapsed) {
			return "dxi dxi-chevron-right";
		}
		if (this._isXDirection() && !this.config.collapsed) {
			return "dxi dxi-chevron-left";
		}
		if (!this._isXDirection() && this.config.collapsed) {
			return "dxi dxi-chevron-up";
		}
		if (!this._isXDirection() && !this.config.collapsed) {
			return "dxi dxi-chevron-down";
		}
	}
	private _isLastCell() {
		const parent = this._parent as any;
		return parent && parent._cells.indexOf(this) === parent._cells.length - 1;
	}
	private _getNextCell() {
		const parent = this._parent as any;
		const index = parent._cells.indexOf(this);
		return parent._cells[index + 1];
	}
	private _getResizerView() {
		return this._parent.getRefs("resizer_" + this._uid);
	}
	private _isXDirection() {
		return this._parent && (this._parent as any)._xLayout;
	}
	private _calculateStyle() {
		const config = this.config;
		if (!config) {
			return;
		}
		const style: any = {};

		if (this._isXDirection()) {
			if (config.width !== undefined && !config.collapsed) {
				style.flexBasis = config.width;
				style.width = config.width;
			}
			if (config.height !== undefined) {
				style.height = config.height;
			}
		} else {
			if (config.height !== undefined && !config.collapsed) {
				style.height = config.height;
			}
			if (config.width !== undefined) {
				style.width = config.width;
			}
		}
		// if (config.padding) {
		// 	style.padding = config.padding;
		// }
		return style;
	}
}